import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CATEGORIES, PRODUCTS, BRAND } from "@/data/products";
import { ArrowUpRight, MessageCircle, Download } from "lucide-react";

export default function Gallery() {
  const [active, setActive] = useState("all");

  const filtered = useMemo(() => {
    if (active === "all") return PRODUCTS;
    return PRODUCTS.filter((p) => p.category === active);
  }, [active]);

  return (
    <section
      id="collections"
      data-testid="gallery-section"
      className="py-24 md:py-32 bg-cream-100"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Section head */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <div className="text-[11px] tracking-[0.32em] uppercase text-gold-dark mb-4">
              — Marble Mandir, Moorti & Sculpture Collection
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink max-w-2xl leading-[1.05]">
              Sixteen Categories.
              <br />
              <span className="italic gold-text">One Sacred Craft.</span>
            </h2>
          </div>
          <div className="flex flex-col gap-5 max-w-md">
            <p className="text-ink-soft text-base leading-relaxed">
              Every piece is carved to order in {BRAND.material}. Browse by
              category and request a bespoke design shaped to your space.
            </p>
            <a
              href="/vardaan-catalogue.pdf"
              download="Vardaan-Arts-Catalogue.pdf"
              data-testid="download-catalogue-btn"
              className="group inline-flex items-center gap-3 self-start px-7 py-3.5 border border-gold text-ink text-[11px] tracking-[0.26em] uppercase hover:bg-gold/10 transition-colors duration-300"
            >
              <Download className="w-4 h-4 text-gold-dark group-hover:translate-y-0.5 transition-transform duration-300" />
              Download Full Catalogue
            </a>
          </div>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-12" data-testid="category-filters">
          {CATEGORIES.map((c) => {
            const isActive = active === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`filter-${c.id}`}
                className={`px-5 py-3 text-[11px] tracking-[0.22em] uppercase border transition-all duration-300 ${
                  isActive
                    ? "bg-ink text-cream-100 border-ink"
                    : "bg-transparent text-ink border-gold/40 hover:border-gold hover:bg-cream-200"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Bento grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: i * 0.06,
                  ease: [0.16, 1, 0.3, 1],
                }}
                data-testid={`product-card-${p.id}`}
                className={`gallery-card group block relative ${
                  i % 7 === 0 ? "sm:col-span-2" : ""
                }`}
              >
                <div className="relative overflow-hidden aspect-[4/5] bg-cream-200">
                  <img
                    src={p.image}
                    alt={`${p.title} — hand-carved Makrana marble ${CATEGORIES.find((c) => c.id === p.category)?.label || "sculpture"} for home by Vardaan Arts`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-deep/90 via-ink-deep/25 to-transparent opacity-95" />
                  {/* content */}
                  <div className="absolute inset-x-0 bottom-0 p-6 md:p-7 flex flex-col gap-4">
                    <div>
                      <div className="text-[10px] tracking-[0.28em] uppercase text-gold-light mb-2">
                        {CATEGORIES.find((c) => c.id === p.category)?.label}
                      </div>
                      <h3 className="font-serif text-2xl md:text-[28px] text-cream-100 leading-tight">
                        {p.title}
                      </h3>
                      <p className="text-cream-100/85 text-sm mt-3 max-w-md leading-relaxed hidden md:block">
                        {p.description}
                      </p>
                    </div>
                    <a
                      href={`https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
                        `Hello Vardaan Arts,\n\nI am interested in your product:\n"${p.title}"\n(Category: ${CATEGORIES.find((c) => c.id === p.category)?.label})\n\nPlease share more details, pricing and customisation options.\n\nThank you.`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`enquiry-btn-${p.id}`}
                      className="inline-flex items-center justify-center gap-2 w-full sm:w-auto self-start px-5 py-3 bg-gold text-ink text-[11px] tracking-[0.24em] uppercase font-medium hover:bg-cream-100 transition-colors duration-300 border border-gold"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Custom Order Enquiry
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {filtered.length === 0 && (
          <div className="text-center py-20 text-ink-soft">
            No products in this category yet.
          </div>
        )}
      </div>
    </section>
  );
}
